import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import type { Document } from 'mongodb'
import { aOrganization } from '~/atoms'
import type { DataLayer, ModelFind } from '~/models'
import type { ApiResponse } from './utils'

type FetchDataLayersResponse = ApiResponse & {
  dataLayers: DataLayer[]
  count: number
  nextOffset: number
}

async function fetchDataLayers({
  find,
  sort,
  project,
  limit,
  offset,
  signal,
}: {
  find: ModelFind
  sort?: { [key: string]: number }
  project?: Document
  limit?: number
  offset?: number
  signal?: AbortSignal
}): Promise<FetchDataLayersResponse> {
  const response = await fetch('/api/data-layers', {
    body: JSON.stringify({
      find,
      sort,
      project,
      limit,
      offset,
    }),
    method: 'POST',
    signal,
  })
  const result = await response.json()
  if (!result.success) {
    console.error('Error loading data layers', result.error)
    throw new Error(`There was a problem fetching results: ${result.error}`)
  }
  return result
}

export function useDataLayersQuery({
  key,
  find,
  sort,
  keepPreviousData = true,
  refetchOnWindowFocus = true,
  refetchOnMount = true,
  refetchInterval,
  enabled,
}: {
  key: string[]
  find?: { [key: string]: any }
  sort?: { [key: string]: number }
  keepPreviousData?: boolean
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
  refetchInterval?: number
  enabled?: boolean
}) {
  const organization = useAtomValue(aOrganization)
  const queryClient = useQueryClient()
  const dataLayersQuery = useQuery({
    queryKey: key,
    queryFn: !find
      ? undefined
      : () =>
          fetchDataLayers({
            find: { ...find, 'organization._id': organization!._id },
            sort,
          }),
    // keepPreviousData,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval,
    enabled: enabled && !!find,
  })
  return {
    isLoading:
      dataLayersQuery.isLoading ||
      dataLayersQuery.isFetching ||
      dataLayersQuery.isRefetching,
    isError: dataLayersQuery.isError,
    dataLayers: dataLayersQuery.data?.dataLayers || [],
    refetch: dataLayersQuery.refetch,
    updateDataLayersData: (
      updateFunc: (previousDataLayers: DataLayer[]) => DataLayer[]
    ) => {
      return queryClient.setQueryData(
        key,
        (previousData?: { success: boolean; dataLayers: DataLayer[] }) => {
          if (!previousData) return undefined
          return {
            ...previousData,
            dataLayers: updateFunc(previousData.dataLayers),
          }
        }
      )
    },
  }
}
